import { Slider, TextField, Typography } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import { isArray } from '@material-ui/data-grid';
import ChangeAllPlacementCard from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCard';
import { DetectionTimeSpecification } from 'Models/Project/ProjectFilterSpecifications';
import { ChangeEvent, ReactElement } from 'react';
import { padNumber } from 'Utils/conversions';

interface ChangeAllPlacementCardTimeProps {
	time: DetectionTimeSpecification | null;
	onTimeChanged: (time: DetectionTimeSpecification | null) => void;
	videoDuration: number;
}

const INITIAL_TIME_VALUE: DetectionTimeSpecification = { from: 0, to: 0 };

const ChangeAllPlacementCardTime = (props: ChangeAllPlacementCardTimeProps): ReactElement => {
	const isChecked = props.time !== null;
	const fromValue = Math.round((props.time?.from ?? 0) / 1000);
	const toValue = Math.round((props.time?.to ?? 0) / 1000);
	const sliderValue = [fromValue, toValue];

	const getValueLabelFormat = (value: number): string => {
		const seconds = padNumber(Math.floor(value % 60), 2);
		const minutes = padNumber(Math.floor(value / 60) % 60, 2);
		const hours = padNumber(Math.floor(value / 3600), 2);
		return `${hours}:${minutes}:${seconds}`;
	};

	const getTimestamp = (value: string): number => {
		const splittedValue = value.split(':');
		const hours = Number(splittedValue[0]);
		const minutes = Number(splittedValue[1]);
		const seconds = Number(splittedValue[2]);
		return (hours * 60 + minutes) * 60 + seconds;
	};

	const startMark = {
		value: 0,
		label: getValueLabelFormat(0),
	};
	const middleMark = {
		value: Math.floor(props.videoDuration / 2),
		label: getValueLabelFormat(props.videoDuration / 2),
	};
	const endMark = {
		value: Math.floor(props.videoDuration),
		label: getValueLabelFormat(props.videoDuration),
	};
	const marks = [startMark, middleMark, endMark];

	const handlePlacementCardCheckedChanged = (checked: boolean): void => {
		if (checked) props.onTimeChanged(INITIAL_TIME_VALUE);
		else props.onTimeChanged(null);
	};

	const setValue = (from: number, to: number): void => {
		props.onTimeChanged({ from: from * 1000, to: to * 1000 });
	};

	const handleSliderChanged = (_: ChangeEvent<{}>, newValue: number | number[]): void => {
		if (isArray(newValue)) setValue(newValue[0], newValue[1]);
		else console.error(`Cannot set angle. Given value '${newValue}' is invalid. Expected array with length 2.`);
	};

	const handleFromChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const timestamp = getTimestamp(target.value);
		if (timestamp <= toValue) setValue(timestamp, toValue);
	};

	const handleToChanged = ({ target }: ChangeEvent<HTMLInputElement>): void => {
		const timestamp = getTimestamp(target.value);
		if (fromValue <= timestamp) setValue(fromValue, timestamp);
	};

	return (
		<ChangeAllPlacementCard
			name="Select time"
			onCheckedChanged={handlePlacementCardCheckedChanged}
			isChecked={isChecked}
		>
			<FormControl fullWidth={true} margin="normal">
				<Typography id="range-slider" gutterBottom>
					Time
				</Typography>
				<Slider
					value={sliderValue}
					onChange={handleSliderChanged}
					defaultValue={0}
					marks={marks}
					valueLabelDisplay="auto"
					valueLabelFormat={getValueLabelFormat}
					min={0}
					max={props.videoDuration}
				/>
			</FormControl>
			<FormControl fullWidth={true} margin="normal">
				<TextField
					id="time"
					label="From"
					type="time"
					inputProps={{
						step: 1,
						max: toValue,
						min: 0,
					}}
					value={getValueLabelFormat(fromValue)}
					onChange={handleFromChanged}
				/>
			</FormControl>
			<FormControl fullWidth={true} margin="normal">
				<TextField
					id="time"
					label="From"
					type="time"
					inputProps={{
						step: 1,
						max: props.videoDuration,
						min: fromValue,
					}}
					value={getValueLabelFormat(toValue)}
					onChange={handleToChanged}
				/>
			</FormControl>
		</ChangeAllPlacementCard>
	);
};

export default ChangeAllPlacementCardTime;
